// frontend/src/context/PlotDetailContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useFarm } from './FarmContext';
import { groupMetrics } from '../constants/metricsRegistry';
import { generateDevicePalette } from '../utils/colorUtils';

const PlotDetailContext = createContext(null);

export const sanitizeIds = (arr) => {
  if (!Array.isArray(arr)) return [];
  return Array.from(
    new Set(
      arr
        .flat()
        .map((id) => (Array.isArray(id) ? id[0] : id))
        .filter((id) => id !== undefined && id !== null && id !== 'undefined' && id !== 'null' && id !== '')
    )
  );
};

export const parseToMs = (timeStr) => {
  if (!timeStr) return 0;
  const str = String(timeStr).trim();
  let ms = Date.parse(str);
  if (!isNaN(ms)) return ms;
  const currentYear = new Date().getFullYear();
  ms = Date.parse(`${currentYear}/${str}`);
  if (!isNaN(ms)) return ms;
  return 0;
};

export const buildNormalizedDevices = (plotMetadata, physicalDevices = []) => {
  if (!plotMetadata) return [];
  const hardwareList = physicalDevices.filter(
    (dev) => !(dev.device_type === 'proxy' || dev.is_proxy || dev.isProxy)
  );
  const hardwareColors = generateDevicePalette(hardwareList.length);
  let hardwareIndex = 0;

  const formattedDevices = physicalDevices.map((dev) => {
    const isProxyDevice = dev.device_type === 'proxy' || dev.is_proxy || dev.isProxy;
    let assignedColor = dev.color;
    if (!assignedColor) {
      if (isProxyDevice) {
        assignedColor = '#475569';
      } else {
        assignedColor = hardwareColors[hardwareIndex] || '#166534';
        hardwareIndex++;
      }
    }
    return {
      id: dev.device_id || dev.id,
      name: dev.name || (isProxyDevice ? `${plotMetadata.name || 'Site'} Regional Proxy` : `Sensor ${dev.device_id ? dev.device_id.slice(0, 6) : ''}`),
      location: dev.location || (isProxyDevice ? 'Regional Station Data' : 'In-field Sensor'),
      isProxy: isProxyDevice,
      deviceType: dev.device_type || (isProxyDevice ? 'proxy' : 'sensor'),
      color: assignedColor,
      status: dev.status || 'ACTIVE',
      description: dev.description || (isProxyDevice ? 'Regional meteorological feed' : 'Telemetry node')
    };
  });

  const hasProxy = formattedDevices.some((d) => d.isProxy);
  if (!hasProxy) {
    formattedDevices.unshift({
      id: plotMetadata.site_id || plotMetadata.id,
      name: `${plotMetadata.name || 'Plot'} Regional Proxy`,
      location: 'Regional Station Data',
      isProxy: true,
      deviceType: 'proxy',
      color: '#475569',
      status: 'ACTIVE',
      description: 'Regional meteorological and satellite synthetic feed'
    });
  }
  return formattedDevices;
};

export const extractValueFromPoint = (pt, metricType, fullKey) => {
  if (!pt) return null;
  const isStage = metricType?.includes('growth_stage') || fullKey?.includes('growth_stage');
  const formatVal = (rawVal) => {
    if (rawVal === undefined || rawVal === null) return null;
    if (isStage) return typeof rawVal === 'object' ? rawVal : String(rawVal);
    const num = Number(rawVal);
    return isNaN(num) ? null : num;
  };
  if (pt.value !== undefined && pt.value !== null) return formatVal(pt.value);
  if (pt.val !== undefined && pt.val !== null) return formatVal(pt.val);
  if (pt.stage !== undefined && pt.stage !== null) return formatVal(pt.stage);
  if (fullKey && pt[fullKey] !== undefined && pt[fullKey] !== null) return formatVal(pt[fullKey]);
  if (metricType && pt[metricType] !== undefined && pt[metricType] !== null) return formatVal(pt[metricType]);
  const fallbackNum = Object.values(pt).find((v) => typeof v === 'number');
  return fallbackNum !== undefined ? formatVal(fallbackNum) : null;
};

export const extractDiscoveredMetricTypes = (timeseries = []) => {
  const discovered = [];
  if (!Array.isArray(timeseries)) return discovered;
  timeseries.forEach((devNode) => {
    const metricsArr = Array.isArray(devNode.metrics) ? devNode.metrics : [];
    metricsArr.forEach((m) => {
      const t = Array.isArray(m.type) ? m.type[0] : m.type;
      const mdl = Array.isArray(m.model) ? m.model[0] : (m.model || 'default');
      const isPhenology = t && (t.includes('degree_days') || t.includes('degree_hours') || t.includes('growth_stage'));
      if (t) {
        const fullType = (isPhenology && mdl && mdl !== 'default') ? `${t}_${mdl}` : t;
        if (!discovered.includes(fullType)) discovered.push(fullType);
      }
    });
  });
  return discovered;
};

export const transformTimeseriesToChartData = (timeseries = []) => {
  if (!Array.isArray(timeseries)) return [];
  const timeMap = {};

  timeseries.forEach((devNode) => {
    const rawDevId = Array.isArray(devNode.deviceId) ? devNode.deviceId[0] : devNode.deviceId;
    if (!rawDevId || rawDevId === 'undefined') return;
    const metricsArr = Array.isArray(devNode.metrics) ? devNode.metrics : [];

    metricsArr.forEach((metricObj) => {
      const metricType = Array.isArray(metricObj.type) ? metricObj.type[0] : metricObj.type;
      const modelName = Array.isArray(metricObj.model) ? metricObj.model[0] : (metricObj.model || 'default');
      if (!metricType) return;

      const isPhenology = metricType.includes('degree_days') || metricType.includes('degree_hours') || metricType.includes('growth_stage');
      const fullMetricKey = (isPhenology && modelName && modelName !== 'default') ? `${metricType}_${modelName}` : metricType;
      const isGrowthStage = metricType.includes('growth_stage');

      const processPoints = (pts, isForecast) => {
        if (!Array.isArray(pts)) return;
        pts.forEach((pt) => {
          const time = pt?.time || pt?.datetime || pt?.timestamp || pt?.date;
          if (!time) return;
          if (!timeMap[time]) {
            timeMap[time] = { time, timestamp: parseToMs(time) };
          }
          const val = extractValueFromPoint(pt, metricType, fullMetricKey);
          if (val === null || val === undefined) return;

          if (isGrowthStage) {
            timeMap[time][fullMetricKey] = val;
          } else {
            const suffix = isForecast ? 'fcst' : 'hist';
            timeMap[time][`${fullMetricKey}_${rawDevId}_${suffix}`] = val;
          }
        });
      };

      processPoints(metricObj.history, false);
      processPoints(metricObj.forecast, true);
    });
  });

  return Object.values(timeMap).sort((a, b) => a.timestamp - b.timestamp);
};

export const PlotDetailProvider = ({ children, farmId: propFarmId, plotId: propPlotId }) => {
  const { selectedFarmId, selectedPlotId, selectedPlotSummary } = useFarm();
  const activeFarmId = propFarmId || selectedFarmId;
  const activePlotId = propPlotId || selectedPlotId;

  const [plotData, setPlotData] = useState(null);
  const [physicalDevices, setPhysicalDevices] = useState([]);
  const [visibleDeviceIds, setVisibleDeviceIds] = useState([]);
  const [activeMetrics, setActiveMetrics] = useState([]);
  const [disabledSections, setDisabledSections] = useState({});

  const [forecastVisible, setForecastVisible] = useState(() => {
    return !window.location.pathname.endsWith('/device');
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPlotDetails = useCallback(async () => {
    if (!activeFarmId || !activePlotId) return;
    setLoading(true);
    setError(null);

    try {
      const [detailRes, devicesRes, prefRes] = await Promise.all([
        axios.get(`/api/farms/${activeFarmId}/plots/${activePlotId}/detail`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`/api/farms/${activeFarmId}/plots/${activePlotId}/devices`, { withCredentials: true }).catch(() => ({ data: { devices: [] } })),
        axios.get(`/api/preferences/plot/${activePlotId}`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);

      const fetchedData = detailRes.data || {};
      const fetchedDevices = devicesRes.data?.devices || [];
      const userPrefs = prefRes.data || {};

      const timeseriesDevIds = sanitizeIds((fetchedData.timeseries || []).map((ts) => ts.deviceId));
      const detailDevIds = sanitizeIds(fetchedData.devices);
      const apiDevIds = sanitizeIds(fetchedDevices.map((d) => (typeof d === 'object' ? d.device_id || d.id : d)));
      const uniquePhysicalIds = sanitizeIds([...apiDevIds, ...detailDevIds, ...timeseriesDevIds]);

      const mergedPhysicalDevs = Array.isArray(fetchedDevices) ? [...fetchedDevices] : [];
      uniquePhysicalIds.forEach((id) => {
        const exists = mergedPhysicalDevs.some((d) => {
          const dId = typeof d === 'object' ? d.device_id || d.id : d;
          return dId === id;
        });
        if (!exists) {
          mergedPhysicalDevs.push({ id, device_id: id, name: `Sensor ${String(id).slice(0, 6)}`, device_type: 'sensor' });
        }
      });

      const discoveredTypes = extractDiscoveredMetricTypes(fetchedData.timeseries);
      setPlotData(fetchedData);
      setPhysicalDevices(mergedPhysicalDevs);

      const normMetadata = fetchedData.metadata || { id: activePlotId, site_id: activePlotId };
      const normDevs = buildNormalizedDevices(normMetadata, mergedPhysicalDevs);
      const allDevIds = normDevs.map((d) => d.id);

      const savedVisibleIds = sanitizeIds(userPrefs.visibleDeviceIds);
      setVisibleDeviceIds(savedVisibleIds.length > 0 ? savedVisibleIds : allDevIds);

      let initialMetrics = userPrefs.activeMetrics;
      if (!initialMetrics || initialMetrics.length === 0) {
        initialMetrics = discoveredTypes.length > 0 ? discoveredTypes : [];
      }
      setActiveMetrics(initialMetrics);
    } catch (err) {
      console.error('Failed to load plot details:', err);
      setError('Failed to load plot data.');
    } finally {
      setLoading(false);
    }
  }, [activeFarmId, activePlotId]);

  useEffect(() => {
    fetchPlotDetails();
  }, [fetchPlotDetails]);

  const devices = useMemo(() => {
    const metadata = plotData?.metadata || selectedPlotSummary || { id: activePlotId, site_id: activePlotId };
    return buildNormalizedDevices(metadata, physicalDevices);
  }, [plotData, selectedPlotSummary, physicalDevices, activePlotId]);

  const validVisibleDeviceIds = useMemo(() => {
    const availableIds = new Set(devices.map((d) => d.id));
    const cleaned = visibleDeviceIds.filter((id) => availableIds.has(id));
    return cleaned.length > 0 ? cleaned : devices.map((d) => d.id);
  }, [devices, visibleDeviceIds]);

  const visibleDevices = useMemo(() => {
    return devices.filter((d) => validVisibleDeviceIds.includes(d.id));
  }, [devices, validVisibleDeviceIds]);

  const chartData = useMemo(() => {
    if (!plotData?.timeseries) return [];
    if (!Array.isArray(plotData.timeseries) && plotData.timeseries.chartData) {
      return [...plotData.timeseries.chartData].sort((a, b) => parseToMs(a.time) - parseToMs(b.time));
    }
    return transformTimeseriesToChartData(plotData.timeseries);
  }, [plotData]);

  const categoryGroups = useMemo(() => {
    if (plotData?.timeseries?.categoryGroups) {
      return plotData.timeseries.categoryGroups;
    }
    const discoveredTypes = extractDiscoveredMetricTypes(plotData?.timeseries);
    return groupMetrics(discoveredTypes.length > 0 ? discoveredTypes : undefined);
  }, [plotData]);

  const toggleDevice = useCallback((deviceId) => {
    setVisibleDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  }, []);

  const toggleMetric = useCallback((metricType) => {
    setActiveMetrics((prev) =>
      prev.includes(metricType) ? prev.filter((m) => m !== metricType) : [...prev, metricType]
    );
  }, []);

  const toggleSection = useCallback((categoryKey) => {
    setDisabledSections((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  }, []);

  const toggleForecast = useCallback((visible) => {
    setForecastVisible(visible !== undefined ? visible : (prev) => !prev);
  }, []);

  const value = {
    plot: plotData || selectedPlotSummary,
    plotData,
    devices,
    visibleDeviceIds: validVisibleDeviceIds,
    visibleDevices,
    activeMetrics,
    chartData,
    categoryGroups,
    forecastVisible,
    disabledSections,
    loading,
    error,
    toggleDevice,
    toggleDeviceVisibility: toggleDevice,
    toggleMetric,
    toggleSection,
    toggleForecast,
    fetchPlotDetails
  };

  return (
    <PlotDetailContext.Provider value={value}>
      {children}
    </PlotDetailContext.Provider>
  );
};

export const usePlotDetailContext = () => {
  const context = useContext(PlotDetailContext);
  if (!context) {
    throw new Error('usePlotDetailContext must be used within a PlotDetailProvider');
  }
  return context;
};
